import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  CheckCircle2,
  ShoppingBag,
  Shield,
  FileText,
  Clock,
} from "lucide-react";
import { formatMoney } from "../../utils/formatMoney";

const tabs = [
  { key: "premium", label: "Policy Premium" },
  { key: "covered", label: "What is covered" },
  { key: "claims", label: "Claim process overview" },
  { key: "terms", label: "Terms & Condition" },
];

export default function PackageDrawer({ open, onClose, item }) {
  const [selectedPkg, setSelectedPkg] = useState("");
  const [activeTab, setActiveTab] = useState("premium");
  const [selectedRouteType, setSelectedRouteType] = useState("regional");
  const navigate = useNavigate();

  const policyType = item?.originalPolicy?.policy_type;
  const shortDescription = item?.shortDescription;
  const isLifeInsurance =
    shortDescription === "life_insurance" || policyType === "life_insurance";
  const isGeneralInsurance =
    shortDescription === "general_insurance" ||
    policyType === "general_insurance";
  const isTravelInsurance =
    shortDescription === "travel_insurance" || policyType === "travel_insurance";

  // Generate dynamic packages from policy data and group by route type
  const { packages, routeTypes } = useMemo(() => {
    if (!item?.originalPolicy?.coverage_durations)
      return { packages: [], routeTypes: [] };

    const isIntercityPolicy =
      item?.originalPolicy?.policy_type === "intercity_travel";

    const allPackages = item.originalPolicy.coverage_durations.map(
      (duration, index) => {
        // Helper to format duration label
        const formatDurationLabel = (duration) => {
          if (duration && typeof duration === "object") {
            // Handle travel insurance duration string (e.g., "24 hour", "1 years")
            if (duration.duration && typeof duration.duration === "string") {
              return duration.duration;
            }
            // Handle new format with display_unit and display_value
            if (duration.display_unit && duration.display_value) {
              const unit = duration.display_unit;
              const value = duration.display_value;
              return `${value} ${value === 1 ? unit.slice(0, -1) : unit}`;
            }
            // Handle old format with days
            if (duration.days) {
              return `${duration.days} ${duration.days === 1 ? "Day" : "Days"}`;
            }
            // Handle hours format
            if (duration.hours) {
              const hours = duration.hours;
              return `${hours} ${hours === 1 ? "Hour" : "Hours"}`;
            }
          }
          return duration ? duration.toString() : "Unknown";
        };

        const getDurationValue = (duration) => {
          if (duration && typeof duration === "object") {
            // Handle travel insurance duration string (e.g., "24 hour", "1 years")
            if (duration.duration && typeof duration.duration === "string") {
              const match = duration.duration.match(/^(\d+)\s*(hour|hours|day|days|month|months|year|years)$/i);
              if (match) {
                return parseInt(match[1], 10);
              }
            }
            if (duration.display_value !== undefined)
              return duration.display_value;
            if (duration.days !== undefined) return duration.days;
            if (duration.hours !== undefined) return duration.hours;
          }
          return 1;
        };

        const getDurationUnit = (duration) => {
          if (duration && typeof duration === "object") {
            // Handle travel insurance duration string (e.g., "24 hour", "1 years")
            if (duration.duration && typeof duration.duration === "string") {
              const match = duration.duration.match(/^\d+\s*(hour|hours|day|days|month|months|year|years)$/i);
              if (match) {
                const unit = match[1].toLowerCase();
                // Normalize to singular form
                if (unit.endsWith('s')) return unit.slice(0, -1);
                return unit;
              }
            }
            if (duration.display_unit) return duration.display_unit;
            if (duration.days !== undefined) return "days";
            if (duration.hours !== undefined) return "hours";
          }
          return "days";
        };

        const durationValue = getDurationValue(duration);
        const durationUnit = getDurationUnit(duration);
        const durationLabel = formatDurationLabel(duration);

        // For intercity policies, include route information in the label
        // For travel insurance, use product_name if available
        let packageLabel;
        if (isIntercityPolicy && duration.route_destination) {
          packageLabel = duration.route_destination;
        } else if (duration.product_name) {
          // Travel insurance packages have product_name
          packageLabel = duration.product_name;
        } else {
          packageLabel = `${durationLabel} Cover`;
        }

        return {
          key: `${index}-${durationValue}${durationUnit.charAt(0)}${
            duration.route_destination
              ? `-${duration.route_destination.replace(/\s+/g, "-")}`
              : ""
          }`,
          label: packageLabel,
          durationLabel: durationLabel,
          price: duration.price || duration.premium || 0,
          days: durationValue,
          unit: durationUnit,
          routeOrigin: isIntercityPolicy ? "Greater Accra" : null,
          routeDestination: duration.route_destination || null,
          routeType: duration.route_type || null,
          active: index === 0,
          fullDuration: duration,
        };
      }
    );

    // Extract unique route types if this is an intercity policy
    const uniqueRouteTypes = isIntercityPolicy
      ? [...new Set(allPackages.map((p) => p.routeType).filter(Boolean))]
      : [];

    return {
      packages: allPackages,
      routeTypes: uniqueRouteTypes,
    };
  }, [
    item?.originalPolicy?.coverage_durations,
    item?.originalPolicy?.policy_type,
  ]);

  // Filter packages by selected route type
  const filteredPackages = useMemo(() => {
    if (routeTypes.length === 0) return packages;
    return packages.filter((pkg) => pkg.routeType === selectedRouteType);
  }, [packages, selectedRouteType, routeTypes]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setActiveTab("premium");

    // Set initial route type if available
    if (routeTypes.length > 0) {
      setSelectedRouteType(routeTypes[0]);
      const firstPackageOfType = packages.find(
        (p) => p.routeType === routeTypes[0]
      );
      setSelectedPkg(firstPackageOfType?.key || "");
    } else {
      setSelectedPkg(packages[0]?.key || "");
    }

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open, item, packages, routeTypes]);

  // Update selected package when route type changes
  useEffect(() => {
    if (
      filteredPackages.length > 0 &&
      !filteredPackages.find((p) => p.key === selectedPkg)
    ) {
      setSelectedPkg(filteredPackages[0]?.key || "");
    }
  }, [selectedRouteType, filteredPackages, selectedPkg]);

  const handleBuyNow = () => {
    const selectedPackage = filteredPackages.find(
      (p) => p.key === selectedPkg
    );
    // Find the selected coverage duration from the original policy
    const selectedCoverageDuration =
      selectedPackage?.fullDuration ||
      item?.originalPolicy?.coverage_durations?.find(
        (duration) => {
          if (
            duration.display_unit &&
            duration.display_value
          ) {
            const matchesDuration =
              duration.display_value ===
                selectedPackage?.days &&
              duration.display_unit === selectedPackage?.unit;
            const matchesRoute =
              !duration.route_destination ||
              duration.route_destination ===
                selectedPackage?.routeDestination;
            return matchesDuration && matchesRoute;
          } else if (duration.days) {
            return duration.days === selectedPackage?.days;
          } else if (duration.hours) {
            return duration.hours === selectedPackage?.days;
          }
          return false;
        }
      );

    const policyType =
      item?.originalPolicy?.policy_type || "daily_commuter";
    const isLife = policyType === "life_insurance" || item?.shortDescription === "life_insurance";
    const isTravel = policyType === "travel_insurance" || item?.shortDescription === "travel_insurance";
    const isGeneral = policyType === "general_insurance" || item?.shortDescription === "general_insurance";
    const insuranceType = isLife ? "life_insurance" : isTravel ? "travel_insurance" : isGeneral ? "general_insurance" : null;

    const details = {
      policyType,
      insuranceType,
      providerName: item?.provider || "",
      policyName: item?.policyName || "",
      coverLabel: selectedPackage?.label || "Cover",
      amount: selectedPackage?.price || selectedPackage?.premium || item?.premiumMin || 0,
      days: selectedPackage?.days ?? 2,
      policySlug: item?.originalPolicy?.slug || item?.id,
      originalPolicy: item?.originalPolicy,
      coverageDuration: selectedCoverageDuration || null,
    };
    navigate("/insurance/checkout", { state: details });
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.aside
            className="fixed inset-y-0 right-0 z-[70] w-full max-w-2xl bg-white shadow-2xl flex flex-col h-full"
            initial={{ x: 420, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 420, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {/* Header */}
            <div className="flex justify-between items-start p-5 border-b border-gray-200/70">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {item?.policyName || item?.provider}
                </h3>
                {item?.originalPolicy?.insurance_provider && (
                  <div className="flex gap-2 items-center mt-1">
                    <span className="text-xs text-gray-500">Provided by:</span>
                    <span className="text-xs font-semibold text-emerald-700">
                      {typeof item.originalPolicy.insurance_provider === 'object' 
                        ? item.originalPolicy.insurance_provider.company_name || item.originalPolicy.insurance_provider.name || "Insurance Provider"
                        : item.originalPolicy.insurance_provider}
                    </span>
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {item?.shortDescription}
                </p>
              </div>
              <button
                onClick={onClose}
                className="flex-shrink-0 p-2 text-gray-500 rounded-lg hover:bg-gray-100"
                aria-label="Close package details"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto overscroll-contain flex-1 p-4 space-y-4 min-h-0">
              {/* Package options - Grid Layout */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Select Coverage
                  </h4>
                  <button
                    onClick={handleBuyNow}
                    className="inline-flex gap-2 items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-green-600 rounded-lg shadow-md transition-all hover:from-emerald-600 hover:to-green-700"
                  >
                    <ShoppingBag size={16} /> Buy Now
                  </button>
                </div>

                {/* Route Type Tabs - Only show for intercity policies with multiple route types */}
                {routeTypes.length > 0 && (
                  <div className="flex overflow-x-auto gap-2 no-scrollbar">
                    {routeTypes.map((routeType) => (
                      <button
                        key={routeType}
                        onClick={() => setSelectedRouteType(routeType)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-all ${
                          selectedRouteType === routeType
                            ? "bg-emerald-500 text-white shadow-md"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {routeType.charAt(0).toUpperCase() + routeType.slice(1)}{" "}
                        Routes
                      </button>
                    ))}
                  </div>
                )}

                {/* Package Grid */}
                <div className="grid grid-cols-2 gap-2">
                  {filteredPackages.map((p) => {
                    const isActive = selectedPkg === p.key;
                    return (
                      <label key={p.key} className="cursor-pointer">
                        <input
                          type="radio"
                          name="pkg"
                          checked={isActive}
                          onChange={() => setSelectedPkg(p.key)}
                          className="sr-only"
                        />
                        <div
                          className={`relative rounded-lg border text-sm transition-all p-3 h-full ${
                            isActive
                              ? "border-emerald-500 ring-2 shadow-md ring-emerald-500/20"
                              : "border-gray-200 hover:border-emerald-300 hover:shadow-sm"
                          }`}
                        >
                          {isActive && (
                            <motion.div
                              layoutId="pkgHighlight"
                              className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg"
                            />
                          )}
                          <div className="flex relative z-10 flex-col gap-1">
                            {p.routeOrigin && p.routeDestination ? (
                              <div className="flex gap-1 items-center">
                                <span
                                  className={`font-semibold text-xs ${
                                    isActive ? "text-emerald-700" : "text-gray-900"
                                  }`}
                                >
                                  {p.routeOrigin}
                                </span>
                                <svg
                                  className={`w-3 h-3 ${
                                    isActive ? "text-emerald-600" : "text-gray-400"
                                  }`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                                  />
                                </svg>
                                <span
                                  className={`font-semibold text-xs ${
                                    isActive ? "text-emerald-700" : "text-gray-900"
                                  }`}
                                >
                                  {p.routeDestination}
                                </span>
                              </div>
                            ) : (
                              <span
                                className={`font-semibold text-sm ${
                                  isActive ? "text-emerald-700" : "text-gray-900"
                                }`}
                              >
                                {p.label}
                              </span>
                            )}
                            {p.routeDestination && (
                              <span className="text-xs text-gray-600">
                                {p.durationLabel}
                              </span>
                            )}
                            <div className="flex gap-1 items-baseline mt-1">
                              <span
                                className={`text-lg font-bold ${
                                  isActive
                                    ? "text-emerald-600"
                                    : "text-gray-900"
                                }`}
                              >
                                {formatMoney(p.price ?? 0)}
                              </span>
                              {p.routeType && (
                                <span className="text-xs text-gray-500 capitalize">
                                  • {p.routeType}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Tabs */}
              <div className="space-y-3">
                <div className="flex overflow-x-auto relative gap-2 items-center whitespace-nowrap border-b border-gray-200 no-scrollbar">
                  {tabs.map((t) => {
                    const active = activeTab === t.key;
                    return (
                      <button
                        key={t.key}
                        onClick={() => setActiveTab(t.key)}
                        className={`relative px-3 py-1.5 text-xs font-medium transition-colors ${
                          active
                            ? "text-emerald-700"
                            : "text-gray-600 hover:text-emerald-600"
                        }`}
                      >
                        {t.label}
                        {active && (
                          <motion.span
                            layoutId="tabUnderline"
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 rounded-full"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>

                {activeTab === "premium" && (
                  <motion.div
                    key="premium"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="p-3 rounded-lg ring-1 ring-emerald-100 bg-emerald-50/60"
                  >
                    <div className="flex gap-2 items-start">
                      <span className="grid place-items-center w-5 h-5 text-emerald-600 bg-emerald-100 rounded-full flex-shrink-0 mt-0.5">
                        <CheckCircle2 size={14} />
                      </span>
                      <div className="flex-1 space-y-2">
                        <p className="text-xs font-semibold text-gray-900">
                          Premium Details
                        </p>
                        <div className="flex flex-wrap gap-1.5 items-center">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-100">
                            {(() => {
                              const selectedPackage = filteredPackages.find(
                                (p) => p.key === selectedPkg
                              );
                              if (selectedPackage) {
                                const price = selectedPackage.price?.toFixed?.(2) || selectedPackage.price || "0.00";
                                // For travel insurance, show premium duration if available
                                const fullDuration = selectedPackage.fullDuration;
                                if (fullDuration?.premium_duration) {
                                  const premiumDuration = fullDuration.premium_duration;
                                  const durationMap = {
                                    'yearly': 'year',
                                    'monthly': 'month',
                                    'weekly': 'week',
                                    'daily': 'day'
                                  };
                                  const durationLabel = durationMap[premiumDuration.toLowerCase()] || premiumDuration;
                                  return `₵${price} / ${durationLabel}`;
                                }
                                // Otherwise show duration label
                                const unit =
                                  selectedPackage.unit === "hours"
                                    ? "hour"
                                    : selectedPackage.unit === "years" || selectedPackage.unit === "year"
                                    ? "year"
                                    : selectedPackage.unit === "months" || selectedPackage.unit === "month"
                                    ? "month"
                                    : "day";
                                const pluralUnit =
                                  selectedPackage.days > 1 ? `${unit}s` : unit;
                                return `${formatMoney(price)} / ${selectedPackage.durationLabel || `${selectedPackage.days} ${pluralUnit}`}`;
                              }
                              return `From ${formatMoney(item?.premiumMin ?? 5)}`;
                            })()}
                          </span>
                          {(() => {
                            const selectedPackage = filteredPackages.find(
                              (p) => p.key === selectedPkg
                            );
                            if (selectedPackage?.routeOrigin && selectedPackage?.routeDestination) {
                              return (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100">
                                  <span>{selectedPackage.routeOrigin}</span>
                                  <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                                    />
                                  </svg>
                                  <span>{selectedPackage.routeDestination}</span>
                                </span>
                              );
                            }
                            if (selectedPackage?.routeDestination) {
                              return (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100">
                                  {selectedPackage.routeDestination}
                                </span>
                              );
                            }
                          })()}
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100">
                            No hidden fees
                          </span>
                        </div>

                        {/* Underwriter Information */}
                        <div className="mt-3 pt-3 border-t border-emerald-200/50">
                          <div className="flex items-center gap-2">
                            <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                              <Shield size={12} className="text-white" />
                            </div>
                            <div className="flex-1">
                              <p className="text-xs font-medium text-gray-700">
                                Underwritten by
                              </p>
                              <p className="text-sm font-bold text-blue-700">
                                {item?.provider}
                              </p>
                            </div>
                            <div className="flex-shrink-0">
                              <span className="inline-flex items-center px-2 py-1 text-[10px] font-semibold text-blue-800 bg-blue-100 rounded-full">
                                Licensed
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {activeTab === "covered" && (
                  <motion.div
                    key="covered"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="p-3 rounded-lg ring-1 ring-emerald-100 bg-emerald-50/60"
                  >
                    <div className="flex gap-2 items-start mb-2">
                      <span className="grid place-items-center w-5 h-5 text-emerald-600 bg-emerald-100 rounded-full flex-shrink-0 mt-0.5">
                        <Shield size={14} />
                      </span>
                      <div>
                        <h4 className="text-xs font-semibold text-gray-900">
                          What is covered
                        </h4>
                        <p className="mt-1 text-xs leading-relaxed text-gray-600">
                          {item?.description ||
                            "This policy provides comprehensive coverage for your travel needs."}
                        </p>
                      </div>
                    </div>
                    <ul className="pl-7 mt-2 space-y-2">
                      {(() => {
                        // For Life, General and Travel insurance, prefer package‑specific coverage
                        if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
                          const selectedPackage = filteredPackages.find(
                            (p) => p.key === selectedPkg
                          );
                          const packageBenefits =
                            selectedPackage?.fullDuration?.insurance_coverage_list;

                          if (Array.isArray(packageBenefits) && packageBenefits.length > 0) {
                            return packageBenefits.map((coverage, index) => (
                              <li
                                key={index}
                                className="flex gap-2 items-start text-xs"
                              >
                                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                                <div>
                                  <span className="font-medium text-gray-900">
                                    {coverage.title}
                                  </span>
                                  {coverage.description && (
                                    <p className="mt-0.5 text-gray-600">
                                      {coverage.description}
                                    </p>
                                  )}
                                  {coverage.limit && (
                                    <p className="mt-0.5 font-medium text-emerald-600">
                                      Limit: ₵{coverage.limit}
                                    </p>
                                  )}
                                </div>
                              </li>
                            ));
                          }
                        }

                        // Fallback to policy‑level coverage list
                        const policyBenefits =
                          item?.originalPolicy?.insurance_coverage_list;
                        if (Array.isArray(policyBenefits) && policyBenefits.length > 0) {
                          return policyBenefits.map((coverage, index) => (
                            <li
                              key={index}
                              className="flex gap-2 items-start text-xs"
                            >
                              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                              <div>
                                <span className="font-medium text-gray-900">
                                  {coverage.title}
                                </span>
                                {coverage.description && (
                                  <p className="mt-0.5 text-gray-600">
                                    {coverage.description}
                                  </p>
                                )}
                                {coverage.limit && (
                                  <p className="mt-0.5 font-medium text-emerald-600">
                                    Limit: ₵{coverage.limit}
                                  </p>
                                )}
                              </div>
                            </li>
                          ));
                        }

                        // Final fallback
                        return (
                          <li className="flex gap-2 items-start text-xs text-gray-900">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                            <span>Comprehensive protection coverage</span>
                          </li>
                        );
                      })()}
                    </ul>
                  </motion.div>
                )}

                {activeTab === "claims" && (
                  <motion.div
                    key="claims"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-3"
                  >
                    <div className="p-3 rounded-lg ring-1 ring-emerald-100 bg-emerald-50/60">
                      <div className="flex gap-2 items-start mb-2">
                        <span className="grid place-items-center w-5 h-5 text-emerald-600 bg-emerald-100 rounded-full flex-shrink-0 mt-0.5">
                          <FileText size={14} />
                        </span>
                        <div>
                          <h4 className="text-xs font-semibold text-gray-900">
                            Claim Process
                          </h4>
                          <p className="mt-0.5 text-xs text-gray-600">
                            Follow these steps to file a claim
                          </p>
                        </div>
                      </div>
                      <ul className="pl-7 mt-2 space-y-2">
                        {(() => {
                          // For Life, General and Travel insurance, prefer package‑specific claim steps
                          if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
                            const selectedPackage = filteredPackages.find(
                              (p) => p.key === selectedPkg
                            );
                            const packageClaimSteps =
                              selectedPackage?.fullDuration?.claim_process_steps;

                            if (
                              Array.isArray(packageClaimSteps) &&
                              packageClaimSteps.length > 0
                            ) {
                              return packageClaimSteps.map((step, index) => (
                                <li
                                  key={index}
                                  className="flex gap-2 items-start text-xs"
                                >
                                  <span className="flex justify-center items-center w-4 h-4 text-[10px] font-semibold text-white bg-emerald-500 rounded-full flex-shrink-0 mt-0.5">
                                    {step.step_number || index + 1}
                                  </span>
                                  <div className="flex-1">
                                    <span className="font-medium text-gray-900">
                                      {step.title}
                                    </span>
                                    {step.description && (
                                      <p className="mt-0.5 text-gray-600">
                                        {step.description}
                                      </p>
                                    )}
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {step.timeframe && (
                                        <span className="px-1.5 py-0.5 text-[10px] text-blue-800 bg-blue-100 rounded">
                                          {step.timeframe}
                                        </span>
                                      )}
                                      {step.requirements && (
                                        <span className="px-1.5 py-0.5 text-[10px] text-gray-800 bg-gray-100 rounded">
                                          {step.requirements}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </li>
                              ));
                            }
                          }

                          // Fallback to policy‑level claim steps
                          const policySteps =
                            item?.originalPolicy?.claim_process_steps;
                          if (Array.isArray(policySteps) && policySteps.length > 0) {
                            return policySteps.map((step, index) => (
                              <li
                                key={index}
                                className="flex gap-2 items-start text-xs"
                              >
                                <span className="flex justify-center items-center w-4 h-4 text-[10px] font-semibold text-white bg-emerald-500 rounded-full flex-shrink-0 mt-0.5">
                                  {step.step_number || index + 1}
                                </span>
                                <div className="flex-1">
                                  <span className="font-medium text-gray-900">
                                    {step.title}
                                  </span>
                                  {step.description && (
                                    <p className="mt-0.5 text-gray-600">
                                      {step.description}
                                    </p>
                                  )}
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {step.timeframe && (
                                      <span className="px-1.5 py-0.5 text-[10px] text-blue-800 bg-blue-100 rounded">
                                        {step.timeframe}
                                      </span>
                                    )}
                                    {step.requirements && (
                                      <span className="px-1.5 py-0.5 text-[10px] text-gray-800 bg-gray-100 rounded">
                                        {step.requirements}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </li>
                            ));
                          }

                          // Final fallback
                          return (
                            <li className="flex gap-2 items-start text-xs text-gray-900">
                              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                              <span>Contact support for claim assistance</span>
                            </li>
                          );
                        })()}
                      </ul>
                    </div>
                  </motion.div>
                )}

                {activeTab === "terms" && (
                  <motion.div
                    key="terms"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-3"
                  >
                    <div className="p-3 rounded-lg ring-1 ring-emerald-100 bg-emerald-50/60">
                      <div className="flex gap-2 items-start mb-2">
                        <span className="grid place-items-center w-5 h-5 text-emerald-600 bg-emerald-100 rounded-full flex-shrink-0 mt-0.5">
                          <Shield size={14} />
                        </span>
                        <h4 className="text-xs font-semibold text-gray-900">
                          Terms & Conditions
                        </h4>
                      </div>
                      <div className="pl-7 text-xs leading-relaxed text-gray-700">
                        {(() => {
                          // For Life, General and Travel insurance, prefer package‑specific terms
                          if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
                            const selectedPackage = filteredPackages.find(
                              (p) => p.key === selectedPkg
                            );
                            const packageTerms = selectedPackage?.fullDuration?.terms;

                            if (packageTerms) {
                              if (
                                typeof packageTerms === "string" &&
                                (packageTerms.startsWith("http://") ||
                                  packageTerms.startsWith("https://"))
                              ) {
                                return (
                                  <a
                                    href={packageTerms}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-emerald-600 hover:underline"
                                  >
                                    View Terms & Conditions
                                  </a>
                                );
                              }
                              return (
                                <p className="whitespace-pre-wrap">{packageTerms}</p>
                              );
                            }
                          }

                          // Fallback to policy‑level terms
                          const policyTerms = item?.terms || item?.originalPolicy?.terms;
                          if (policyTerms) {
                            if (
                              typeof policyTerms === "string" &&
                              (policyTerms.startsWith("http://") ||
                                policyTerms.startsWith("https://"))
                            ) {
                              return (
                                <a
                                  href={policyTerms}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-emerald-600 hover:underline"
                                >
                                  View Terms & Conditions
                                </a>
                              );
                            }
                            return (
                              <p className="whitespace-pre-wrap">{policyTerms}</p>
                            );
                          }

                          return (
                            <p>
                              Please refer to the policy document for complete terms
                              and conditions.
                            </p>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="p-3 rounded-lg ring-1 ring-amber-100 bg-amber-50/60">
                      <div className="flex gap-2 items-start mb-2">
                        <span className="grid place-items-center w-5 h-5 text-amber-600 bg-amber-100 rounded-full flex-shrink-0 mt-0.5">
                          <FileText size={14} />
                        </span>
                        <div>
                          <h4 className="text-xs font-semibold text-gray-900">
                            Eligibility Criteria
                          </h4>
                          <p className="mt-0.5 text-[10px] text-gray-600">
                            Requirements for this policy
                          </p>
                        </div>
                      </div>
                    <ul className="space-y-1.5 pl-7">
                      {(() => {
                        // For Life, General and Travel insurance, prefer package‑specific eligibility
                        if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
                          const selectedPackage = filteredPackages.find(
                            (p) => p.key === selectedPkg
                          );
                          const packageEligibility =
                            selectedPackage?.fullDuration?.eligibility_criteria;

                          if (
                            Array.isArray(packageEligibility) &&
                            packageEligibility.length > 0
                          ) {
                            return packageEligibility.map((criteria, index) => (
                              <li
                                key={index}
                                className="flex gap-2 items-start text-xs"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                                <span className="text-gray-700">{criteria}</span>
                              </li>
                            ));
                          }
                        }

                        // Fallback to policy‑level eligibility
                        const policyEligibility =
                          item?.eligibilityCriteria ||
                          item?.originalPolicy?.eligibility_criteria;
                        if (
                          Array.isArray(policyEligibility) &&
                          policyEligibility.length > 0
                        ) {
                          return policyEligibility.map((criteria, index) => (
                            <li
                              key={index}
                              className="flex gap-2 items-start text-xs"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                              <span className="text-gray-700">{criteria}</span>
                            </li>
                          ));
                        }

                        return (
                          <li className="flex gap-2 items-start text-xs">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                            <span className="text-gray-700">
                              Standard eligibility requirements apply
                            </span>
                          </li>
                        );
                      })()}
                    </ul>
                    </div>

                    <div className="p-3 rounded-lg ring-1 ring-red-100 bg-red-50/60">
                      <div className="flex gap-2 items-start mb-2">
                        <span className="grid place-items-center w-5 h-5 text-red-600 bg-red-100 rounded-full flex-shrink-0 mt-0.5">
                          <FileText size={14} />
                        </span>
                        <div>
                          <h4 className="text-xs font-semibold text-gray-900">
                            Exclusions
                          </h4>
                          <p className="mt-0.5 text-[10px] text-gray-600">
                            Situations not covered
                          </p>
                        </div>
                      </div>
                    <ul className="space-y-1.5 pl-7">
                      {(() => {
                        // For Life, General and Travel insurance, prefer package‑specific exclusions
                        if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
                          const selectedPackage = filteredPackages.find(
                            (p) => p.key === selectedPkg
                          );
                          const packageExclusions =
                            selectedPackage?.fullDuration?.exclusions;

                          if (
                            Array.isArray(packageExclusions) &&
                            packageExclusions.length > 0
                          ) {
                            return packageExclusions.map((exclusion, index) => (
                              <li
                                key={index}
                                className="flex gap-2 items-start text-xs"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                                <span className="text-gray-700">{exclusion}</span>
                              </li>
                            ));
                          }
                        }

                        // Fallback to policy‑level exclusions
                        const policyExclusions =
                          item?.exclusions || item?.originalPolicy?.exclusions;
                        if (
                          Array.isArray(policyExclusions) &&
                          policyExclusions.length > 0
                        ) {
                          return policyExclusions.map((exclusion, index) => (
                            <li
                              key={index}
                              className="flex gap-2 items-start text-xs"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                              <span className="text-gray-700">{exclusion}</span>
                            </li>
                          ));
                        }

                        return (
                          <li className="flex gap-2 items-start text-xs">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                            <span className="text-gray-700">
                              Standard exclusions apply as per policy terms
                            </span>
                          </li>
                        );
                      })()}
                    </ul>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

